/**
 * @file Saga creating one contract with its parties and charge templates: a 201 updates the store and snackbars, a 207 opens the create's partial dialog.
 * Failure path: catch → ErrorHandlerSequence with the route's expected errors → GLOBAL_CONTRACT_CREATE_FAILURE.
 */
import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ErrorHandlerSequence, ExpectedErrors } from "../Sequences/ErrorHandlerSequence";
import Http from "../../Http/Http";
import Time from "../../Time/Time";
import ContractFactory from "../../Entity/Factories/ContractFactory";
import ChargeTemplateFactory from "../../Entity/Factories/ChargeTemplateFactory";
import { ActionInterface } from "../../Interface/ActionInterface";
import { ChargeTemplateInterface } from "../../Entity/Entities/Charge/ChargeTemplateInterface";
import { MainTenant } from "../../Entity/Entities/ContactForContract/Type/Types/MainTenant";
import { isContractIdOnly } from "../../Entity/Entities/Contract/PartialAnswer";
import Translator from "../../Translation/Translator";
// import { AgencyFeeInterface } from "../../Entity/Entities/AgencyFee/AgencyFeeInterface";
// import AgencyFeeFactory from "../../Entity/Factories/AgencyFeeFactory";

const EXPECTED_ERRORS: ExpectedErrors = {
  be_ajv_not_validated: null,
  be_main_tenant_missing: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-tenant-no-contact" },
  cannot_set_end_date_to_unsigned_contract: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-end-date" },
  be_last_earlier_than_first_date: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-last-date" },
  be_final_rental_period_date_early: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-final-rental-date" },
  cant_create_contract_without_rent_template: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-no-rent-template" },
  first_date_is_required: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-template-first-date" },
  be_server_error: { titleKey: "refusal-title-contract-create", bodyKey: "refusal-body-contract-create-server-error" },
  user_cant_have_a_contract: { titleKey: "refusal-title-contract-create", bodyKey: "user_cant_have_a_contract" }
};

export default function* contractCreate(action: ActionInterface): any {
  try {
    yield put({
      type: Constants.GLOBAL_CONTRACT_CREATE_ERRORS_CLEAR
    });

    yield put({
      type: Constants.GLOBAL_CONTRACT_CREATE_REQUEST
    });

    const contactForContracts: any[] = [];

    // First, we have to push Tenant into the array of Persons.
    if (!!action.payload?.selectedPerson) {
        contactForContracts.push({
        contactId: action.payload?.сreatedFromCoach ? null : action.payload?.selectedPerson?.id || null,
        contractPartyDescription: "Tenant",
        contactForContractTypeId: MainTenant.contactForContractTypeId
      });
    }
    
    // If we have additional fields, we have to push those into the array too.
    action.payload?.additionalFields?.forEach(field => {
      if (!field) {
        return;
      }

      if (typeof field?.selectedPerson === "undefined") {
        return;
      }

      contactForContracts.push({
        contactId: field.selectedPerson && field?.selectedPerson?.id,
        contractPartyDescription: field.description,
        contactForContractTypeId: field.contactForContractTypeId
      });
    });

    let chargeTemplates;
    
    if(action.payload?.сreatedOnFrontend){
      chargeTemplates = action.payload?.chargeTemplates?.map((chargeTemplate: ChargeTemplateInterface) => {
        return {
          amount: chargeTemplate?.amount,
          currencyId: chargeTemplate?.currency,
          type: chargeTemplate?.type,
          repeatPeriodId: chargeTemplate?.repeatPeriod,
          description: chargeTemplate?.description,
          paymentOffset: chargeTemplate?.paymentOffset,
          dueDateFirstPayment: Time.formatOrNull(chargeTemplate?.dueDateFirstPayment),
          firstDate: Time.formatOrNull(chargeTemplate?.firstDate),
          endDate: Time.formatOrNull(chargeTemplate?.endDate),
          paymentRouteId: chargeTemplate?.paymentRouteId,
          contactId: chargeTemplate.contactId,
          companyId: chargeTemplate.companyId,
          isCollective: chargeTemplate?.isCollective
        };
      })
    } else {
      action.payload?.chargeTemplates?.map((chargeTemplate: ChargeTemplateInterface) => {
        return {
          id: chargeTemplate.id,
          amount: chargeTemplate.amount,
          currency: chargeTemplate.currency.getCurrencyId(),
          type: chargeTemplate.type.id,
          repeatPeriod: chargeTemplate?.repeatPeriod?.id,
          description: chargeTemplate.description,
          paymentOffset: chargeTemplate.paymentOffset?.getValue(),
          dueDateFirstPayment: Time.formatOrNull(chargeTemplate.dueDateFirstPayment),
          firstDate: Time.formatOrNull(chargeTemplate.firstDate),
          endDate: Time.formatOrNull(chargeTemplate.endDate),
          paymentRouteId: chargeTemplate.paymentRouteId,
          contactId: chargeTemplate.contactId,
          companyId: chargeTemplate.companyId,
          isCollective: chargeTemplate?.isCollective
        };
      })
    }

    const res = yield call(Http.post, `contract`, {
      unitId: action.payload?.unit && action.payload?.unit.id,
      amazonFolderName: action.payload?.amazonFolderName,
      contractStatusId: action.payload?.status,
      note: action.payload?.note,
      referenceForTenant: action.payload?.referenceForTenant,
      checkinDate: null,
      checkoutDate: null,
      contactForContracts: contactForContracts,
      mainTenantContactId: action.payload?.selectedPerson?.id,
      mainTenantCompanyId: action.payload?.selectedCompany?.id,
      firstDate: Time.formatOrNull(action.payload?.firstDate),
      lastDate: Time.formatOrNull(action.payload?.lastDate),
      endDate: Time.formatOrNull(action.payload?.endDate),
      expirationDate: Time.formatOrNull(action.payload?.expirationDate),
      finalRentalPeriodDate: Time.formatOrNull(action.payload?.finalRentalPeriodDate),
      chargeTemplates: chargeTemplates,
      amazonFoldernameTenant: action.payload?.сreatedFromCoach ? action.payload?.amazonFoldernameTenant : undefined,
      mainContactFirstName: action.payload?.сreatedFromCoach ? action.payload?.mainContactFirstName: undefined,
      mainContactLastName: action.payload?.сreatedFromCoach ? action.payload?.mainContactLastName: undefined,
      autoSendInvoiceToTenant: action.payload?.autoSendInvoiceToTenant,
      isCommercial: action.payload?.isCommercial,
      // agencyFee: !!action.payload?.agencyFee ? {
      //   fixedFee: action.payload?.agencyFee.fixedFee,
      //   percentageFee: action.payload?.agencyFee.percentageFee,
      //   currency: action.payload?.agencyFee.currency.getCurrencyId(),
      //   fixedPlusPercentageMode: action.payload?.agencyFee.fixedPlusPercentageMode.id
      // } : null
    });

    const body = res.data.data;
    const isIdOnly = isContractIdOnly(body);

    yield (isIdOnly ? [] : body.chargeTemplates).map(rawChargeTemplate => {
      const chargeTemplate: ChargeTemplateInterface = ChargeTemplateFactory.create(rawChargeTemplate);

      return put({
        type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_SUCCESS,
        payload: {
          chargeTemplate: chargeTemplate
        }
      });
    });

    // if (res.data.agencyFee) {
    //   const agencyFee: AgencyFeeInterface = AgencyFeeFactory.create(res.data.agencyFee);

    //   yield put({
    //     type: Constants.GLOBAL_AGENCY_FEE_CREATE_SUCCESS,
    //     payload: {
    //       agencyFee: agencyFee
    //     }
    //   });
    // }

    const contract: any = isIdOnly ? body : ContractFactory.create(body);

    if (!isIdOnly) {
      yield put({
        type: Constants.GLOBAL_CONTRACT_CREATE_SUCCESS,
        payload: {
          contract: contract
        }
      });
    }

    if (res.status === 207) {
      yield put({
        type: Constants.GLOBAL_REFUSAL_DIALOG_OPEN,
        payload: {
          titleKey: "partial-title-contract-create",
          bodyKey: "partial-body-contract-create"
        }
      });
    } else {
      yield put({
        type: Constants.SNACKBAR_ADD_MESSAGE,
        payload: {
          message: Translator.trans("snackbar-message-contract-created-successfully"),
          type: "success"
        }
      });
    }

    if (action.hasOwnProperty("onSuccess") === true) {
      action.onSuccess.call(this, contract);
    }
  } catch (err) {
    yield ErrorHandlerSequence(err, action, EXPECTED_ERRORS);

    yield put({
      type: Constants.GLOBAL_CONTRACT_CREATE_FAILURE,
      payload: {
        err: err
      }
    });
  }
}

export function* watchContractCreate() {
  yield takeEvery(Constants.GLOBAL_CONTRACT_CREATE_START, contractCreate);
}
